import { getAuth } from "firebase/auth";
import { app } from "../../../firebase-config.js";
import { ChartManager } from "../../../components/charts/ChartManager.js"; // Import ChartManager
import { APP_VERSION } from "../../../version.js";

const auth = getAuth(app);

// Inject Styles for Marquee (Robust Method)
const styleId = 'analytics-styles';
if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
        @keyframes marquee {
            0% { transform: translateX(100%); }
            100% { transform: translateX(-100%); }
        }
        .animate-marquee {
            animation: marquee 30s linear infinite;
        }
        .animate-marquee:hover {
            animation-play-state: paused;
        }
    `;
    document.head.appendChild(style);
}

// Helper for Flags
const getFlagEmoji = (lang) => {
    const map = {
        'ko': '🇰🇷', 'en': '🇺🇸', 'ja': '🇯🇵', 'zh': '🇨🇳', 'vi': '🇻🇳',
        'th': '🇹🇭', 'es': '🇪🇸', 'fr': '🇫🇷', 'de': '🇩🇪', 'ru': '🇷🇺'
    };
    return map[lang] || '🌐';
};

export const OverviewTab = async (container) => {
    const user = auth.currentUser;

    // 1. Initial Loading State (Skeleton)
    container.innerHTML = `
        <div class="space-y-6 animate-fade-in pb-12">
            <!-- Header Skeleton -->
            <div class="flex flex-col md:flex-row justify-between gap-4 mb-4">
                <div class="h-8 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                <div class="h-8 bg-gray-200 rounded w-1/4 animate-pulse"></div>
            </div>
            
            <!-- Basic Metrics Skeleton -->
             <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div class="h-32 bg-gray-100 rounded-xl animate-pulse"></div>
                <div class="h-32 bg-gray-100 rounded-xl animate-pulse"></div>
                <div class="h-32 bg-gray-100 rounded-xl animate-pulse"></div>
                <div class="h-32 bg-gray-100 rounded-xl animate-pulse"></div>
            </div>

            <!-- Charts Skeleton -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div class="h-64 bg-gray-100 rounded-xl animate-pulse"></div>
                <div class="h-64 bg-gray-100 rounded-xl animate-pulse"></div>
            </div>

            <!-- Ticker Skeleton -->
            <div class="h-10 bg-gray-100 rounded-full w-full animate-pulse mb-6"></div>

            <!-- Grid Skeleton -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div class="h-40 bg-gray-100 rounded-xl animate-pulse"></div>
                <div class="h-40 bg-gray-100 rounded-xl animate-pulse"></div>
                <div class="h-40 bg-gray-100 rounded-xl animate-pulse"></div>
            </div>
        </div>
    `;

    try {
        const { getFirestore, collection, query, orderBy, limit, getDocs } = await import("firebase/firestore");
        const { AdminOverviewService } = await import("../../../services/admin-overview.service.js");

        // --- 1. Fetch & Process all data via Service ---
        const data = await AdminOverviewService.fetchOverviewData();
        const {
            totalUsers,
            totalSeries,
            seriesMetrics,
            userMetrics,
            velocityList,
            pulseItems,
            chartData,
            localeStats // New
        } = data;

        // Destructure sub-objects for easier usage in template
        const { totalViews, riskCount, topRiskTitle, aiPercentage, aiCount, adultCount, globalAvgRating, topSeries } = seriesMetrics;
        const { pendingCount, returningUsers, loyalUsers, dau, returnRate, loyalRate } = userMetrics;
        const { labels: chartLabels, viewData, userData } = chartData;

        // --- 5. Render Dashboard ---
        container.innerHTML = `
            <div class="animate-fade-in pb-12">
                <!-- Header -->
                <div class="flex flex-col md:flex-row justify-between items-end mb-6 gap-4">
                    <div>
                        <h2 class="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
                            <span class="material-symbols-outlined text-indigo-600 text-3xl">hub</span> COMMAND CENTER
                        </h2>



                        <p class="text-xs text-gray-500 font-mono mt-1">OPERATIONAL INTELLIGENCE ${APP_VERSION}</p>
                    </div>
                        <span class="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
                        Live Data: ${new Date().toLocaleTimeString()}
                    </span>
                </div>

                <!-- LOCALE STATS TICKER (Product Hunt Special) -->
                ${localeStats && localeStats.length > 0 ? `
                <div class="mb-6 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-lg p-3 text-white shadow-md flex items-center justify-between animate-fade-in">
                    <div class="flex items-center gap-3">
                        <span class="bg-white/20 p-1.5 rounded text-xs font-bold uppercase tracking-wider">🌏 Global Live</span>
                        <div class="flex items-center gap-4 text-sm font-mono">
                            ${localeStats.map(stat => `
                                <div class="flex items-center gap-1.5">
                                    <span class="text-lg">${getFlagEmoji(stat.lang)}</span>
                                    <span class="font-bold opacity-90">${stat.lang.toUpperCase()}</span>
                                    <span class="bg-white/20 px-1.5 rounded text-xs">+${stat.count}</span>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                    <button onclick="window.router.navigate('/admin?tab=analytics')" class="text-xs bg-white/10 hover:bg-white/20 px-3 py-1 rounded transition-colors">
                        View Details &rarr;
                    </button>
                </div>
                ` : ''}

                <!-- 0. BASIC METRICS (ESSENTIAL - RESTORED) -->
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                     <!-- Total Users -->
                    <div class="bg-white p-5 rounded-xl border border-gray-100 shadow-sm border-l-4 border-l-indigo-500">
                        <p class="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">총 회원수 (Total Users)</p>
                        <div class="flex items-end gap-2">
                            <h3 class="text-3xl font-black text-gray-900">${totalUsers.toLocaleString()}</h3>
                            <span class="text-xs font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded mb-1">명</span>
                        </div>
                    </div>
                    
                    <!-- Total Views -->
                    <div class="bg-white p-5 rounded-xl border border-gray-100 shadow-sm border-l-4 border-l-blue-500">
                        <p class="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">총 방문/조회 (Total Views)</p>
                        <div class="flex items-end gap-2">
                            <h3 class="text-3xl font-black text-gray-900">${totalViews.toLocaleString()}</h3>
                             <span class="text-xs font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded mb-1">회</span>
                        </div>
                    </div>

                    <!-- Total Series -->
                    <div class="bg-white p-5 rounded-xl border border-gray-100 shadow-sm border-l-4 border-l-purple-500">
                         <p class="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">보유 작품수 (Contents)</p>
                         <div class="flex items-end gap-2">
                            <h3 class="text-3xl font-black text-gray-900">${totalSeries.toLocaleString()}</h3>
                            <span class="text-xs font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded mb-1">개</span>
                        </div>
                    </div>

                    <!-- Returning Users -->
                     <div class="bg-white p-5 rounded-xl border border-gray-100 shadow-sm border-l-4 border-l-green-500">
                        <p class="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">재방문 유저 (Returning)</p>
                        <div class="flex items-end gap-2">
                            <h3 class="text-3xl font-black text-gray-900">${returningUsers.toLocaleString()}</h3>
                            <span class="text-xs font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded mb-1">명</span>
                        </div>
                         <p class="text-xs text-gray-400 mt-2">Rate: ${returnRate}%</p>
                    </div>
                </div>

                <!-- 1. TRENDS (CHARTS) -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                     <!-- Views Trend -->
                    <div class="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                        <div class="flex justify-between items-center mb-4">
                            <h3 class="font-bold text-gray-700 flex items-center gap-2">
                                <span class="material-symbols-outlined text-indigo-500">trending_up</span>
                                Daily Views
                            </h3>
                            <span class="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded">Last 14 Days</span>
                        </div>
                        <div class="h-64 relative">
                            <canvas id="chart-views"></canvas>
                        </div>
                    </div>
                    
                    <!-- Users Trend -->
                    <div class="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                        <div class="flex justify-between items-center mb-4">
                            <h3 class="font-bold text-gray-700 flex items-center gap-2">
                                <span class="material-symbols-outlined text-green-500">group_add</span>
                                New Users
                            </h3>
                             <span class="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded">Last 14 Days</span>
                        </div>
                        <div class="h-64 relative">
                            <canvas id="chart-users"></canvas>
                        </div>
                    </div>
                </div>

                <!-- LIVE TICKER (Mirinae Pulse) -->
                <div class="bg-gray-900 text-gray-300 text-sm py-2 px-4 rounded-lg mb-8 shadow-md overflow-hidden flex items-center gap-3 border border-gray-800">
                    <span class="text-xs font-bold text-indigo-400 uppercase tracking-wider shrink-0">⚡ Live Pulse:</span>
                    <div class="flex-1 overflow-hidden relative h-5">
                       <div class="absolute w-full animate-marquee whitespace-nowrap">
                           ${pulseItems.length > 0
                ? pulseItems.map(item => `<span class="mr-8 text-xs font-mono opacity-80">[${item.time.toLocaleTimeString()}] ${item.text}</span>`).join('')
                : '<span class="text-xs text-gray-500">대기 중... (실시간 활동을 모니터링하고 있습니다)</span>'
            }
                       </div>
                    </div>
                </div>

                <!-- 2. ACTION PRIORITY MATRIX -->
                <h3 class="text-sm font-bold text-gray-500 uppercase mb-4 flex items-center gap-2">
                    <span class="material-symbols-outlined">priority_high</span> Action Required
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    
                    <!-- A. Author Requests -->
                    <div class="bg-white rounded-xl p-5 border-l-4 ${pendingCount > 0 ? 'border-l-indigo-500 shadow-md ring-1 ring-indigo-100' : 'border-l-gray-300 border-gray-100'} cursor-pointer hover:bg-gray-50 transition-all group"
                         onclick="window.router.navigate('/admin/requests')">
                        <div class="flex justify-between items-start mb-2">
                            <div>
                                <p class="text-xs font-bold text-gray-500 uppercase">Author Requests</p>
                                <h4 class="text-2xl font-black text-gray-900 mt-1">${pendingCount}</h4>
                            </div>
                            <span class="material-symbols-outlined ${pendingCount > 0 ? 'text-indigo-500 animate-bounce' : 'text-gray-300'}">person_add</span>
                        </div>
                         <p class="text-xs ${pendingCount > 0 ? 'text-indigo-600 font-bold' : 'text-gray-400'} flex items-center gap-1">
                            ${pendingCount > 0 ? `⚠️ 승인 대기 중인 작가가 있습니다.` : '모든 요청이 처리되었습니다.'}
                        </p>
                    </div>

                    <!-- B. Churn Alert -->
                    <div class="bg-white rounded-xl p-5 border-l-4 ${riskCount > 0 ? 'border-l-red-500 shadow-md ring-1 ring-red-100' : 'border-l-gray-300 border-gray-100'}">
                        <div class="flex justify-between items-start mb-2">
                            <div>
                                <p class="text-xs font-bold text-gray-500 uppercase">Risk Monitor</p>
                                <h4 class="text-2xl font-black text-gray-900 mt-1">${riskCount} <span class="text-sm font-normal text-gray-400">Series</span></h4>
                            </div>
                            <span class="material-symbols-outlined ${riskCount > 0 ? 'text-red-500' : 'text-gray-300'}">warning</span>
                        </div>
                         <p class="text-xs text-gray-500">
                            ${riskCount > 0 ? `위험 감지: ${topRiskTitle}` : '안전 상태'}
                        </p>
                    </div>
                </div>

                <!-- 3. ADVANCED METRICS -->
                <h3 class="text-sm font-bold text-gray-500 uppercase mb-4 flex items-center gap-2">
                    <span class="material-symbols-outlined">analytics</span> Platform Vitality
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    
                    <!-- A. Content Velocity -->
                    <div class="bg-gradient-to-br from-indigo-900 to-blue-900 rounded-xl p-6 text-white shadow-lg relative overflow-hidden group">
                        <div class="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                            <span class="material-symbols-outlined text-8xl">rocket_launch</span>
                        </div>
                        <p class="text-xs font-bold text-indigo-300 uppercase mb-1">🔥 Content Velocity (실시간 급상승)</p>
                        ${velocityList.length > 0 ? `
                             <div class="mt-4 space-y-3 relative z-10">
                                <div class="flex items-center gap-3">
                                    <span class="text-2xl font-black text-white">#1</span>
                                    <div>
                                        <div class="font-bold text-sm truncate max-w-[150px]">${velocityList[0][0]}</div>
                                        <div class="text-xs text-indigo-200">+${velocityList[0][1]} views/hr</div>
                                    </div>
                                </div>
                            </div>
                        ` : `
                            <div class="mt-8 text-indigo-200 text-sm relative z-10">
                                <span class="block mb-1 opacity-50">데이터 수집 중...</span>
                            </div>
                        `}
                    </div>

                    <!-- C. Activation (DAU) -->
                    <div class="bg-white rounded-xl p-6 border border-gray-100 shadow-sm relative overflow-hidden">
                        <p class="text-xs font-bold text-gray-500 uppercase mb-1">👥 Active Users (DAU)</p>
                        <h3 class="text-3xl font-black text-gray-900 tracking-tight mt-2 flex items-baseline gap-1">
                            ${dau} <span class="text-sm font-medium text-gray-400">/ ${totalUsers}</span>
                        </h3>
                         <div class="mt-4 pt-4 border-t border-gray-50 text-xs text-gray-500">
                             오늘(${new Date().toISOString().split('T')[0]}) 접속한 유저 수
                        </div>
                    </div>

                    <!-- D. AI & Quality Insight (New) -->
                    <div class="bg-white rounded-xl p-6 border border-gray-100 shadow-sm relative overflow-hidden">
                        <p class="text-xs font-bold text-gray-500 uppercase mb-1">🤖 AI Saturation & Quality</p>
                        <div class="flex items-end justify-between mt-2">
                             <div>
                                <h3 class="text-3xl font-black text-gray-900 tracking-tight">${aiPercentage}%</h3>
                                <p class="text-xs text-gray-400">AI Content (${aiCount} Series)</p>
                            </div>
                            <div class="text-right">
                                <h3 class="text-3xl font-black text-yellow-500 tracking-tight flex items-center gap-1 justify-end">
                                    ${globalAvgRating} <span class="text-sm text-gray-400">/ 5.0</span>
                                </h3>
                                <p class="text-xs text-gray-400">Avg Satisfaction</p>
                            </div>
                        </div>
                        <div class="mt-4 w-full bg-gray-100 rounded-full h-1.5 overflow-hidden flex">
                            <div class="bg-purple-500 h-full" style="width: ${aiPercentage}%" title="AI Portion"></div>
                            <div class="bg-green-500 h-full opacity-50" style="width: ${100 - aiPercentage}%" title="Human Portion"></div>
                        </div>
                    </div>

                    <!-- E. Adult Content (New) -->
                    <div class="bg-white rounded-xl p-6 border border-gray-100 shadow-sm relative overflow-hidden">
                        <p class="text-xs font-bold text-gray-500 uppercase mb-1">🔞 Adult Content Regulation</p>
                        <div class="flex items-end justify-between mt-2">
                             <div>
                                <h3 class="text-3xl font-black text-gray-900 tracking-tight">${adultCount}</h3>
                                <p class="text-xs text-gray-400">19+ Series</p>
                            </div>
                            <div class="text-right">
                                <span class="material-symbols-outlined text-red-500 text-4xl opacity-20">18_up_rating</span>
                            </div>
                        </div>
                         <div class="mt-4 pt-4 border-t border-gray-50 text-xs text-gray-500 flex items-center gap-2">
                             <span class="w-2 h-2 rounded-full bg-red-500"></span> Active Regulation
                        </div>
                    </div>

                </div>

                <div class="text-center text-xs text-gray-300 font-mono mt-8">
                    MIRINAE_OS_V4.1 // READY
                </div>
            </div>
        `;

        // --- 6. Initialize Charts ---
        setTimeout(() => {
            if (window.Chart) {
                // Fallback if Module fails? No, ChartManager imports 'chart.js/auto'.
            }
            ChartManager.createLineChart('chart-views', { labels: chartLabels, values: viewData }, 'Daily Views', '#6366f1');
            ChartManager.createBarChart('chart-users', { labels: chartLabels, values: userData }, 'New Signups', '#10b981');
        }, 100);

    } catch (e) {
        console.error(e);
        container.innerHTML = `<div class="p-10 text-center text-red-500">Dashboard Failure: ${e.message}</div>`;
    }
};
